// SEO service — consultant-first positioning for Level2Logic.
//
// Defaults assume the visitor is a CMMC Registered Practitioner or
// compliance consultant. Page-specific overrides tweak title /
// description for their primary intent.
//
// All strings here must comply with the honest-positioning rule:
//   - We are a self-assessment tool, not a C3PAO.
//   - No fabricated user counts, ratings, or testimonials.
//   - Plain English wherever possible.
//   - Prices come from data/pricing.ts (ADR-0029), never typed in here.

import { LOWEST_PRICE_LABEL, PRICING_PLANS, formatPrice } from '../data/pricing'

const PRICING_DESCRIPTION =
  'Three plans for CMMC consultants, priced on the clients you manage: ' +
  PRICING_PLANS.map((p) => `${p.name} ${formatPrice(p.priceMonthly)}/mo (${p.maxClients} clients)`).join(', ') +
  '. Free for founding partners through 31 January 2027.'

const SITE_NAME = import.meta.env.VITE_APP_SITE_NAME || 'Level2Logic'
const BASE_URL = import.meta.env.VITE_APP_BASE_URL || 'https://level2logic.com'
const DEFAULT_OG_IMAGE = '/og-image.png'

const consultantKeywords = [
  'CMMC compliance software',
  'CMMC software for consultants',
  'CMMC assessment platform',
  'CMMC Registered Practitioner tools',
  'multi-tenant CMMC tool',
  'CMMC gap assessment tool',
  'CMMC Level 2 software',
  'CMMC consultant platform',
  'NIST 800-171 assessment tool',
  'CMMC SSP generator',
  'CMMC POA&M software',
  'CWE to CMMC mapping',
  'config scanner CMMC',
  'CMMC compliance for defense contractors',
  'threat-informed CMMC assessment',
  'MITRE ATT&CK CMMC mapping',
  'NIST CSF to CMMC crosswalk',
  'Terraform CMMC config scanner',
  'AI CMMC remediation guidance',
]

const contractorKeywords = [
  'CMMC self-assessment',
  'CMMC Level 2 checklist',
  'CMMC 110 practices',
  'NIST 800-171',
  'DoD contractor compliance',
  'SPRS score calculator',
  'CMMC readiness',
  'CUI protection',
  'CMMC AI guidance',
  'plain English CMMC practices',
]

interface PageSeoOptions {
  clientCount?: number
  complianceScore?: number
  name?: string
}

export class SEOService {
  static generatePageSEO(pageType: string, data?: PageSeoOptions) {
    const baseSEO = {
      title: 'CMMC Compliance Software for Consultants | Level2Logic',
      description:
        `The CMMC assessment platform consultants use to manage 20 clients in the time it takes to manage 3. From ${LOWEST_PRICE_LABEL}. Threat-informed gap analysis (MITRE ATT&CK), AI practice guidance, CWE/Terraform config scanning, SSP/POA&M.`,
      keywords: [...consultantKeywords, ...contractorKeywords],
    }

    switch (pageType) {
      case 'landing':
        return {
          ...baseSEO,
          title: 'CMMC Compliance Software for Consultants | Level2Logic',
          description:
            `Run CMMC Level 2 readiness for 20 clients in the time it took for 3. Walkthrough of 110 practices, evidence manager, SSP/POA&M auto-generation, CWE→CMMC mapping. From ${LOWEST_PRICE_LABEL}.`,
        }
      case 'consultant':
        return {
          ...baseSEO,
          title: 'CMMC Software for Consultants | Run 20 Engagements at Once',
          description:
            'Multi-tenant CMMC platform built for Registered Practitioners. Manage every client from one portfolio dashboard with cross-client MITRE ATT&CK threat exposure. SSP and POA&M generated from your assessment data.',
        }
      case 'self-assessment':
        return {
          ...baseSEO,
          title: 'CMMC Level 2 Self-Assessment | All 110 Practices',
          description:
            'Walk through all 110 CMMC Level 2 practices in plain English, with AI guidance and the exact evidence a C3PAO expects per practice. Real-time SPRS score and a threat-informed view of which gaps to close first.',
          keywords: [...contractorKeywords, ...consultantKeywords],
        }
      case 'assessment':
        return {
          ...baseSEO,
          title: `Assessment — ${data?.name || 'CMMC Level 2'} | Level2Logic`,
          description: `Real-time SPRS score and gap analysis for ${data?.name || 'a CMMC Level 2 assessment'}.${data?.complianceScore ? ` Current: ${data.complianceScore}/110.` : ''}`,
        }
      case 'dashboard':
        return {
          ...baseSEO,
          title: 'Consultant Portfolio | Level2Logic',
          description:
            'Real-time CMMC readiness across every client in your portfolio, plus portfolio-wide MITRE ATT&CK threat exposure and the fixes that help the most clients. Score, gaps, and status at a glance.',
        }
      case 'subscription':
        return {
          ...baseSEO,
          title: `CMMC Software Pricing — From ${LOWEST_PRICE_LABEL} | Level2Logic`,
          description: PRICING_DESCRIPTION,
        }
      case 'pricing':
        return {
          ...baseSEO,
          title: `CMMC Software Pricing — From ${LOWEST_PRICE_LABEL} | Level2Logic`,
          description: PRICING_DESCRIPTION,
        }
      case 'reports':
        return {
          ...baseSEO,
          title: 'Reports & Exports | Level2Logic',
          description:
            'Generate gap analysis CSV, SSP, and POA&M from your assessment data.',
        }
      case 'resources':
        return {
          ...baseSEO,
          title: 'Free CMMC Resources | Level2Logic',
          description:
            'Free CMMC checklists, compliance guides, and templates for defense contractors and consultants.',
        }
      // /vs-drata and /vs-vanta are retired — they named multi-framework GRC
      // platforms sold to mid-market SaaS companies as our opponents, which is
      // not who a Registered Practitioner loses a CMMC deal to. Both routes now
      // redirect; see src/data/comparisons.ts and ASSESSMENT-2026-09-17 §7.
      case 'vs-futurefeed':
        return {
          ...baseSEO,
          title: 'FutureFeed vs Level2Logic for CMMC | Per-Contractor vs Per-Portfolio',
          description:
            'Both are CMMC-native. FutureFeed lists $99-$399/mo per contractor plus a $1,008/yr Level 2 add-on; Level2Logic prices the whole consultant portfolio at one published figure.',
        }
      case 'vs-cyturus':
        return {
          ...baseSEO,
          title: 'Cyturus vs Level2Logic for CMMC | Channel Position vs Artefacts',
          description:
            'Cyturus is the Cyber AB-designated readiness tool for the RPO channel and does not publish pricing. Compare on the SSP, POA&M and evidence index each one actually produces.',
        }
      case 'vs-spreadsheets':
        return {
          ...baseSEO,
          title: 'Spreadsheets vs Level2Logic for CMMC | Where the Sheet Stops Working',
          description:
            'A spreadsheet is fine for one assessment. It has no opinion about which practices 32 CFR 170.21 bars from a POA&M, and no link between the SPRS score you submit and the evidence behind it.',
        }
      case 'sprs-calculator':
        return {
          ...baseSEO,
          title: 'Free SPRS Score Calculator | CMMC Level 2 | Level2Logic',
          description:
            'Calculate your SPRS score (-203 to 110) using the official DoD Assessment Methodology Annex A weights. Instant results, domain-by-domain breakdown, no signup required.',
          keywords: [...contractorKeywords, ...consultantKeywords],
        }
      case 'readiness-score':
        return {
          ...baseSEO,
          title: 'Free CMMC Readiness Score | 10 Questions, 2 Minutes | Level2Logic',
          description:
            'Where do you stand on CMMC Level 2? Answer 10 quick questions and get your readiness score, recommended tier, and next steps. No signup required.',
          keywords: [...contractorKeywords],
        }
      case 'checklist':
        return {
          ...baseSEO,
          title: 'CMMC Level 2 110-Practice Checklist | Free Download | Level2Logic',
          description:
            'Complete checklist of all 110 CMMC Level 2 practices, organized by domain. Free CSV download. Use with your C3PAO assessment or self-assessment preparation.',
          keywords: [...contractorKeywords],
        }
      case 'features':
        return {
          ...baseSEO,
          title: 'Platform Features | CMMC Level 2 Assessment & Consulting | Level2Logic',
          description:
            'All 110 CMMC Level 2 practices, multi-client dashboard, AI evidence guidance, SSP/POA&M generation, SPRS scoring, contractor self-service, and Stripe billing. Everything you need for CMMC compliance.',
          keywords: [...consultantKeywords, ...contractorKeywords],
        }
      default:
        return baseSEO
    }
  }

  static generateStructuredData(type: string, data?: any) {
    const baseSoftware = {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: SITE_NAME,
      url: BASE_URL,
      description:
        'Multi-tenant CMMC Level 2 readiness platform for compliance consultants and defense contractors.',
      applicationCategory: 'BusinessApplication',
      applicationSubCategory: 'Compliance Management',
      operatingSystem: 'Web Browser',
      offers: {
        '@type': 'AggregateOffer',
        priceCurrency: 'USD',
        lowPrice: String(Math.min(...PRICING_PLANS.map((p) => p.priceMonthly))),
        highPrice: String(Math.max(...PRICING_PLANS.map((p) => p.priceMonthly))),
        offerCount: PRICING_PLANS.length,
      },
      featureList: [
        'Walkthrough of 110 CMMC Level 2 practices',
        'Real-time SPRS score',
        'Evidence manager with practice linking',
        'Auto-generated System Security Plan (SSP)',
        'Plan of Action & Milestones (POA&M)',
        'CWE / MITRE ATT&CK → CMMC mapping',
        'Config scanner (Docker, nginx, Kubernetes)',
        'AI-assisted practice guidance',
      ],
    }

    switch (type) {
      case 'landing':
      case 'software':
        return baseSoftware
      case 'pricing':
        return {
          ...baseSoftware,
          // The public ladder (ADR-0029), from data/pricing.ts.
          offers: PRICING_PLANS.map((plan) => ({
            '@type': 'Offer',
            name: plan.name,
            price: String(plan.priceMonthly),
            priceCurrency: 'USD',
            description: `${formatPrice(plan.priceMonthly)}/month. Up to ${plan.maxClients} clients. ${plan.audience}`,
          })),
        }
      case 'organization':
        return {
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: SITE_NAME,
          url: BASE_URL,
          logo: `${BASE_URL}/favicon.svg`,
          sameAs: [],
          contactPoint: [
            {
              '@type': 'ContactPoint',
              contactType: 'sales',
              email: 'sales@level2logic.com',
              availableLanguage: ['English'],
            },
          ],
        }
      case 'faq':
        return {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: (data?.faq || defaultFaq).map((q: any) => ({
            '@type': 'Question',
            name: q.question,
            acceptedAnswer: { '@type': 'Answer', text: q.answer },
          })),
        }
      default:
        return baseSoftware
    }
  }

  static generateBreadcrumbs(items: Array<{ name: string; url: string }>) {
    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: items.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: item.url,
      })),
    }
  }

  static getCanonical(path: string): string {
    return `${BASE_URL}${path.startsWith('/') ? path : `/${path}`}`
  }

  static getOgImage(): string {
    return DEFAULT_OG_IMAGE
  }
}

export const defaultFaq = [
  {
    question: 'Does Level2Logic certify me for CMMC Level 2?',
    answer:
      'No. Level2Logic is a self-assessment readiness platform. CMMC Level 2 certification requires a formal assessment by an authorized C3PAO. Our tool gets you ready, accurately and quickly, before that formal assessment.',
  },
  {
    question: 'How is Level2Logic different from Drata or Vanta?',
    answer:
      `Drata and Vanta are multi-framework GRC platforms ($10K-$25K/yr) built for SaaS companies. Level2Logic is purpose-built for CMMC and starts at ${LOWEST_PRICE_LABEL}. We add features they do not have: CWE/MITRE auto-mapping to CMMC practices, config scanning for Docker/nginx/K8s, and a consultant-first portfolio dashboard.`,
  },
  {
    question: 'Can my consultants use this with their own clients?',
    answer:
      'Yes. Every plan is built for that, priced on how many clients you manage. One login manages a portfolio of contractor clients, each with their own assessment, evidence, SSP, and POA&M.',
  },
  {
    question: 'What is a SPRS score?',
    answer:
      'SPRS (Supplier Performance Risk System) is the DoD database where contractors submit their NIST 800-171 self-assessment score. The score is on a -203 to 110 scale: 110 means every requirement is met; 88 or above is the score gate for a conditional status with a POA&M, provided no open requirement worth more than 1 point is left on it (32 CFR 170.21).',
  },
  {
    question: 'Do you store CUI?',
    answer:
      'No. Our terms prohibit uploading CUI, network diagrams, or credentials. Evidence text is limited and CUI markings are rejected. We are a planning and tracking tool, not a CUI repository.',
  },
  {
    question: 'How does the AI guidance work?',
    answer:
      'For each of the 110 practices, you can ask Claude (Anthropic) for a plain-English explanation, suggested evidence, and a quick assessment of evidence text you have written. We never send actual CUI to the AI — only the practice ID and a short, sanitized summary.',
  },
]
